import { z } from 'zod'
import { SPONSORSHIP_PLANS } from '@/constants/sponsorshipPlans'
import {
  eventSponsorshipPackageSchema,
  zodMaxAttendeeSchema,
  zodPriceSchema,
} from './eventForm.schema'

// Sponsorship package schema
export const subEventSponsorshipSchema = (fieldName: string) => {
  return z.object({
    id: z.string().optional(),
    name: z.enum(Object.values(SPONSORSHIP_PLANS)),
    price: zodPriceSchema(fieldName),
  })
}

// Sub event ticket schema (for paid events only)
export const subEventTicketSchema = z
  .object({
    id: z.string().optional(),
    name: z
      .string({ error: 'Ticket name is required' })
      .min(1, 'Ticket name is required')
      .max(255, 'Ticket name must be at most 255 characters'),
    description: z
      .string({ error: 'Ticket description is required' })
      .min(1, 'Ticket description is required')
      .max(500, 'Ticket description must be at most 500 characters'),
    perks: z
      .string({ error: 'Ticket perks are required' })
      .min(1, 'Ticket perks are required'),
    price: z.string().nonempty('Ticket price is required').nullable(),
    isNew: z.boolean().optional(),
  })
  .superRefine((ticket, ctx) => {
    if (
      ticket.price === null ||
      ticket.price === undefined ||
      Number(ticket.price) < 0
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Ticket price must be greater or equal to 0',
        path: ['price'],
      })
    } else if (
      ticket.price === null ||
      ticket.price === undefined ||
      Number(ticket.price) > 99999999.99
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Ticket price must not exceed $99,999,999.99',
        path: ['price'],
      })
    }
  })

// Main sub event form schema
export const subEventFormSchema = z.object({
  eventName: z
    .string({ error: 'Event name is required' })
    .min(1, 'Event name is required')
    .max(255, 'Event name must be at most 255 characters'),
  eventDescription: z
    .string({ error: 'Event description is required' })
    .min(1, 'Event description is required')
    .max(500, 'Event description must be at most 500 characters'),
  registrationType: z.enum(['free', 'paid']),
  maxAttendees: zodMaxAttendeeSchema('Max attendees limit'),
  sponsorshipPackages: z.array(eventSponsorshipPackageSchema),
})

export const freeRegistrationSchema = subEventFormSchema.extend({
  registrationType: z.literal('free'),
  ticketName: z
    .string({ error: 'Ticket name is required' })
    .min(1, 'Ticket name is required')
    .max(255, 'Ticket name must be at most 255 characters'),
  ticketDescription: z
    .string({ error: 'Ticket description is required' })
    .min(1, 'Ticket description is required')
    .max(500, 'Ticket description must be at most 500 characters'),
  ticketPerks: z
    .string({ error: 'Ticket perks are required' })
    .min(1, 'Ticket perks are required'),
  eventTicket: z
    .object({
      id: z.string().optional(),
      name: z.string().optional(),
      description: z.string().optional(),
      perks: z.string().optional(),
      price: z.string().optional().nullable(),
      isNew: z.boolean().optional(),
    })
    .partial()
    .optional(),
})

export const paidRegistrationSchema = subEventFormSchema.extend({
  registrationType: z.literal('paid'),
  ticketName: z.string().optional().nullable(),
  ticketDescription: z.string().optional().nullable(),
  ticketPerks: z.string().optional().nullable(),
  eventTicket: subEventTicketSchema,
})

export const registrationBasedSubEventSchema = z.discriminatedUnion(
  'registrationType',
  [freeRegistrationSchema, paidRegistrationSchema]
)

export type SubEventFormDataType = z.infer<
  typeof registrationBasedSubEventSchema
>

// Default values
export const defaultSubEventTicket = {
  id: '',
  description: '',
  name: '',
  perks: '',
  price: '',
  isNew: true,
}

export const defaultPresentingPlan = {
  name: SPONSORSHIP_PLANS.PRESENTING,
  price: '',
}

export const defaultProgramOwnerPlan = {
  name: SPONSORSHIP_PLANS.PROGRAM_OWNER,
  price: '',
}

export const defaultSupportingPlan = {
  name: SPONSORSHIP_PLANS.SUPPORTING,
  price: '',
}

export const eventDatesSchema = z.object({
  id: z.string(),
  startDateTime: z.date(),
  endDateTime: z.date(),
  eventDurationInDays: z.number(),
})

export type EventDatesDataType = z.infer<typeof eventDatesSchema>
